import { EventEmitter, Injectable, inject } from '@angular/core';
import { BehaviorSubject, map, Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { Apollo } from 'apollo-angular';
import { CREATE_CONVERSATION, ASSET_EVENT_SUBSCRIPTION } from '../graphql/data-copilot.operation';

export type CopilotRunModeDraft = 'ORCHESTRATED' | 'AGENT';

export interface CopilotPendingDraft {
  copilotRunMode: CopilotRunModeDraft;
  attachedDatascopeIds: string[];
  attachedMcpConnectionIds: string[];
}

interface AssetEventPayload {
  conversationId: string;
  assetType: 'MESSAGE' | 'QUESTION' | 'ANSWER';
  assetId: string;
  creator: string;
  creatorProfileUrl: string;
  timestamp: string;
  parameters: {
    GENERATED_SQL?: string;
    SECURED_SQL?: string;
    APPROVED_SQL?: string;
    MESSAGE?: string;
    DATA?: any[];
    ERROR?: string;
    quoteText?: string;
    quoteId?: string;
    INSIGHTS?: any;
  };
}

@Injectable({
  providedIn: 'root',
})
export class DataCopilotApiService {
  private http = inject(HttpClient);
  private readonly apollo = inject(Apollo);
  private dataCopilotConversationDataSubject = new BehaviorSubject<any>({
    selectedConversation: null,
    isEmptyStateLoading: false,
    isEmptyState: false,
    conversationsData: [],
    selectedModelName: '',
    selectedModelLogo: '',
  });
  dataCopilotConversationData$ = this.dataCopilotConversationDataSubject.asObservable();
  public newConversationCreated = new EventEmitter<void>();
  public openNewChatRequested = new EventEmitter<void>();
  private selectedScopeSubject = new BehaviorSubject<any>(null);
  public selectedScope$ = this.selectedScopeSubject.asObservable();
  private conversationExpandedSubject = new BehaviorSubject<boolean>(true);
  public conversationExpanded$ = this.conversationExpandedSubject.asObservable();

  /** One-shot draft from the parent new-chat wizard, consumed when the composer loads. */
  private pendingCopilotDraft: CopilotPendingDraft | null = null;
  private pendingCopilotDraftSubject = new BehaviorSubject<CopilotPendingDraft | null>(null);
  public pendingCopilotDraft$ = this.pendingCopilotDraftSubject.asObservable();

  consumePendingCopilotDraft(): CopilotPendingDraft | null {
    const next = this.pendingCopilotDraft;
    console.log('[DataCopilotService] consumePendingCopilotDraft called - draft:', next ? 'EXISTS' : 'NULL', next);
    console.trace('[DataCopilotService] consumePendingCopilotDraft stack trace');
    this.pendingCopilotDraft = null;
    this.pendingCopilotDraftSubject.next(null); // Clear the observable too
    return next;
  }

  /** Peek at pending draft without consuming it. */
  peekPendingCopilotDraft(): CopilotPendingDraft | null {
    return this.pendingCopilotDraft;
  }

  /** True before the child composer consumes the draft (wizard confirm in flight). */
  hasPendingCopilotDraft(): boolean {
    return this.pendingCopilotDraft != null;
  }
  
  setPendingCopilotDraft(draft: CopilotPendingDraft | null): void {
    console.log('[DataCopilotService] setPendingCopilotDraft called - draft:', draft);
    console.trace('[DataCopilotService] setPendingCopilotDraft stack trace');
    this.pendingCopilotDraft = draft;
    this.pendingCopilotDraftSubject.next(draft); // Emit the draft to subscribers
  }

  /** Current data scope hint for Copilot home (paired with outgoing messages). */
  peekSelectedScope(): any {
    return this.selectedScopeSubject.value;
  }

  setSelectedScope(scope: any) {
    this.selectedScopeSubject.next(scope);
    this.updateMatchingDataScopes();
  }

  setConversationExpanded(expanded: boolean) {
    this.conversationExpandedSubject.next(expanded);
  }

  public createConversation(conversation: any): Observable<any> {
    return this.apollo.mutate<any>({
      mutation: CREATE_CONVERSATION,
      variables: { createConversationInput: conversation },
      fetchPolicy: 'no-cache',
    });
  }

  public subscribeToAssetEvents(conversationId: string): Observable<AssetEventPayload> {
    console.log(`Subscribing to all asset events for conversation: ${conversationId}`);
    return this.apollo
      .subscribe<{ onAssetEvent: AssetEventPayload }>({
        query: ASSET_EVENT_SUBSCRIPTION,
        variables: { conversationId },
      })
      .pipe(map((result: any) => result?.data?.onAssetEvent));
  }

  public nlpApi(data: any): Observable<any> {
    return this.http.post(`${environment.api}/nlp/ai`, data);
  }

  /**
   * Consume NDJSON-ish SSE frames from POST /nlp/ai/chat/agent/stream (engine proxied).
   */
  public async consumeNlpAgentStream(
    body: Record<string, unknown>,
    onEvent: (evt: Record<string, unknown>) => void,
    signal?: AbortSignal,
  ): Promise<void> {
    const token = localStorage.getItem('tif-access-token');
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      'tif-user-data': localStorage.getItem('tif-user-data') || '',
    };
    headers['x-tif-txn-id'] =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random()}`;

    const res = await fetch(`${environment.api}/nlp/ai/chat/agent/stream`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
      signal,
    });

    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(text || `Stream failed (${res.status})`);
    }
    if (!res.body) {
      throw new Error('No response body');
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buf = '';
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      let sep: number;
      while ((sep = buf.indexOf('\n\n')) >= 0) {
        const rawBlock = buf.slice(0, sep);
        buf = buf.slice(sep + 2);
        const payload = rawBlock
          .split('\n')
          .filter((line) => line.startsWith('data:'))
          .map((line) => line.slice(5).trim())
          .join('\n')
          .trim();
        if (!payload.length) continue;
        try {
          onEvent(JSON.parse(payload));
        } catch {
          /* skip partial / bad frame */
        }
      }
    }
  }

  setConversationData(selectedConversation: any) {
    const currentData = this.dataCopilotConversationDataSubject.value;
    this.dataCopilotConversationDataSubject.next({
      ...currentData,
      selectedConversation,
    });
    this.updateMatchingDataScopes();
  }

  setEmptyStateLoading(isEmptyStateLoading: any) {
    this.dataCopilotConversationDataSubject.next({
      ...this.dataCopilotConversationDataSubject.value,
      isEmptyStateLoading,
    });
  }

  setEmptyState(isEmptyState: any) {
    this.dataCopilotConversationDataSubject.next({
      ...this.dataCopilotConversationDataSubject.value,
      isEmptyState,
    });
  }

  setSelectedConversationDataWithMatchingDataScope(conversationsData: any) {
    this.dataCopilotConversationDataSubject.next({
      ...this.dataCopilotConversationDataSubject.value,
      conversationsData,
    });
    this.updateMatchingDataScopes();
  }

  public getPredefinedPrompts(data: any): Observable<any> {
    return this.http.post(`${environment.api}/nlp/ai/suggested-prompts`, data);
  }

  public getInsights(data: any): Observable<any> {
    return this.http.post(`${environment.api}/nlp/ai/get-data-insights`, data);
  }

  public getFollowUpPrompts(data: any): Observable<any> {
    return this.http.post(`${environment.api}/nlp/ai/follow-up-prompts`, data);
  }

  updateSelectedModel(name: string, logo: string): void {
    this.dataCopilotConversationDataSubject.next({
      ...this.dataCopilotConversationDataSubject.value,
      selectedModelName: name,
      selectedModelLogo: logo,
    });
  }

  notifyNewConversationCreated() {
    this.newConversationCreated.emit();
  }

  requestOpenNewChatWizard(): void {
    this.openNewChatRequested.emit();
  }

  public reportChat(data: any): Observable<any> {
    return this.http.post(`${environment.api}/nlp/ai/reports/chat`, data);
  }

  public canvasChat(data: any): Observable<any> {
    return this.http.post(`${environment.api}/nlp/ai/canvas/chat`, data);
  }

  public getScopeOverview(data: any): Observable<any> {
    return this.http.post(`${environment.api}/nlp/ai/scope-overview`, data);
  }

  private updateMatchingDataScopes() {
    const currentData = this.dataCopilotConversationDataSubject.value;
    const selectedScopeId = currentData.selectedConversation?.parameters?.DATASCOPES?.[0]?.id;
  
    const updatedConversations = currentData.conversationsData.map((conversation: any) => {
      const hasMatchingScope = selectedScopeId
        ? conversation.parameters?.DATASCOPES?.some((ds: any) => ds.id === selectedScopeId)
        : false;
      return {
        ...conversation,
        hasMatchingDataScope: hasMatchingScope,
      };
    });
  
    this.dataCopilotConversationDataSubject.next({
      ...currentData,
      conversationsData: updatedConversations,
    });
  }
}
